"use client"

import { useEffect, useRef } from "react"

declare global {
    interface Window {
        turnstile?: {
            render: (el: HTMLElement, opts: Record<string, unknown>) => string
            remove: (id: string) => void
            reset: (id?: string) => void
        }
        onloadTurnstileCallback?: () => void
    }
}

const SCRIPT_ID = "cf-turnstile-script"
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"

/** Load the Turnstile script once per page, no matter how many widgets mount. */
function loadScript(): Promise<void> {
    if (typeof window === "undefined") return Promise.resolve()
    if (window.turnstile) return Promise.resolve()

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
    if (existing) {
        return new Promise((resolve, reject) => {
            existing.addEventListener("load", () => resolve())
            existing.addEventListener("error", () => reject(new Error("script failed")))
        })
    }

    return new Promise((resolve, reject) => {
        const script = document.createElement("script")
        script.id = SCRIPT_ID
        script.src = SCRIPT_SRC
        script.async = true
        script.defer = true
        script.onload = () => resolve()
        script.onerror = () => reject(new Error("script failed"))
        document.head.appendChild(script)
    })
}

interface TurnstileWidgetProps {
    siteKey: string
    onVerify: (token: string) => void
    onExpire: () => void
    onError: (message: string) => void
    /** Follows the app theme so the widget does not glare in dark mode. */
    theme?: "auto" | "light" | "dark"
}

/**
 * Cloudflare Turnstile, rendered explicitly.
 *
 * Explicit render rather than the auto `cf-turnstile` class: this mounts inside
 * a dialog, and auto-render only scans on script load — a widget that appears
 * later never gets picked up.
 */
export function TurnstileWidget({
    siteKey,
    onVerify,
    onExpire,
    onError,
    theme = "auto",
}: TurnstileWidgetProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const widgetIdRef = useRef<string | null>(null)

    // The callbacks change identity on every parent render; holding them in a
    // ref keeps the widget from being torn down and re-rendered underneath the
    // reader mid-challenge. Written in an effect rather than during render —
    // mutating a ref while rendering is not safe under concurrent React.
    const handlers = useRef({ onVerify, onExpire, onError })
    useEffect(() => {
        handlers.current = { onVerify, onExpire, onError }
    }, [onVerify, onExpire, onError])

    useEffect(() => {
        let cancelled = false

        loadScript()
            .then(() => {
                if (cancelled || !containerRef.current || !window.turnstile) return
                widgetIdRef.current = window.turnstile.render(containerRef.current, {
                    sitekey: siteKey,
                    theme,
                    callback: (token: string) => handlers.current.onVerify(token),
                    "expired-callback": () => handlers.current.onExpire(),
                    "timeout-callback": () => handlers.current.onExpire(),
                    "error-callback": () =>
                        handlers.current.onError(
                            "The verification check could not load. Please refresh and try again."
                        ),
                })
            })
            .catch(() =>
                handlers.current.onError(
                    "The verification check could not load. Check your connection and try again."
                )
            )

        return () => {
            cancelled = true
            if (widgetIdRef.current && window.turnstile) {
                try {
                    window.turnstile.remove(widgetIdRef.current)
                } catch {
                    // Already gone with the dialog; nothing to clean up.
                }
                widgetIdRef.current = null
            }
        }
    }, [siteKey, theme])

    return <div ref={containerRef} className="flex justify-center" />
}
